import { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { formatDollars } from '../../utils/money';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Grid,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Fab,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  Stack,
  ListItemButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  LocationOn as LocationOnIcon,
  Sms as SmsIcon,
  EmailOutlined as EmailOutlinedIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import HoofIcon from '../../components/HoofIcon';
import Autocomplete from '@mui/material/Autocomplete';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { Client, ClientContact, ClientStatus, HorseClientRole, clientApi, Horse, billingApi, InvoiceStats, creditsApi } from '../../utils/api';
import IssueCreditDialog from '../../components/IssueCreditDialog';
import { reminderService } from '../../services/reminderService';
import { formatBreed } from '../../utils/breed';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentMethodsList from '../../components/PaymentMethodsList';
import SavePaymentMethodDialog from '../../components/SavePaymentMethodDialog';
import ClientNotesPanel from '../../components/ClientNotesPanel';
import ClientContactsEditor from '../../components/ClientContactsEditor';
import { withOfflineFallback, skipWhenOffline } from '../../offline/offlineQuery';
import { useIsOffline } from '../../offline/OfflineNotice';
import { readClientsPage, readClient } from '../../offline/readers';
import { executeWrite, executeUpdate, OfflineWriteBlockedError } from '../../offline/executeWrite';
import { useRBAC } from '../../auth/useRBAC';
import { clientsControls } from '../../auth/pageControls';
import { patientLabel } from '../../utils/patientLabel';

// Stable fallback for the owners query: an inline `= []` default would be a
// fresh reference every render while the query is in flight, re-triggering
// the prefill effect into a render storm.
const NO_OWNER_LINKS: Array<{ clientId: string; name: string; ownershipPercentage: number | null }> = [];

const Clients: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPage(0);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedSearch(value), 300);
  }, []);
  useEffect(() => () => clearTimeout(debounceTimer.current), []);

  // Which write controls this person may see (INV-108). The whole hook STATE goes in, not just
  // `hasPermission`: `<ScreenRoute>`'s wait belongs to the WRAPPER's instance, and this page has
  // its own — a second `useRBAC()` with its own token round trip, null on its first render
  // (INV-107 trap (a)). The map's own entry point holds every control open until this instance's
  // answer is in, and shows them all if it never comes — see `auth/pageControls.ts`.
  const rbac = useRBAC();
  const can = clientsControls(rbac);

  const [statusFilter, setStatusFilter] = useState<ClientStatus | 'all'>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [issueCreditOpen, setIssueCreditOpen] = useState(false); // BILL-CRED
  const [isLinkHorseDialogOpen, setIsLinkHorseDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState<{
    clientName: string;
    horseCount: number;
    horses: Array<{ id: string; name: string; isPrimaryOwner: boolean }>;
    hasHistory: boolean;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saveCardDialogOpen, setSaveCardDialogOpen] = useState(false);
  // null = unknown (probe not run yet / skipped offline) — distinct from a
  // confirmed "Stripe not set up" false, so we never show the set-up-Stripe
  // message just because the status probe couldn't reach the server.
  const [stripeConnectEnabled, setStripeConnectEnabled] = useState<boolean | null>(null);
  const isOffline = useIsOffline();
  // Every date this page renders is an INSTANT (Client.smsConsentAt — DATE-KINDS
  // §1.3; Invoice.issuedAt behind lastInvoiceDate — §1.4), so each reads in the
  // CLINIC's zone, never the browser's. Not a calendar day anywhere on this page.
  const formatInstant = useFormatInstant();
  const [sortField, setSortField] = useState<'name' | 'email' | 'createdAt'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    billingAddress: '',
    status: ClientStatus.ACTIVE,
    smsEnabled: true,
    emailEnabled: true,
    contacts: [] as ClientContact[],
    // SMS consent (ADR-0022) — NOT part of the client PATCH; posted to the
    // dedicated server-stamped endpoint when changed on save.
    smsConsent: false,
    smsConsentSource: '',
  });

  // Link horse state
  const [linkHorseData, setLinkHorseData] = useState<{
    horseId: string;
  }>({
    horseId: '',
  });
  // Ownership allocation for the link dialog: clientId -> percentage text.
  // HorseClient percentages drive invoice splits, so linking a co-owner must
  // state where EVERY owner's percentage lands.
  const [linkAllocations, setLinkAllocations] = useState<Record<string, string>>({});

  // Fetch clients — server-side paginated. Previously this fetched EVERY
  // client in the clinic (and their horses + invoice balances), which was
  // one of the top causes of the "show nothing with lots of data" hang on
  // clinics with many clients.
  // SMS consent UI is platform-gated (ADR-0022): it appears only once the
  // TWILIO_* envs are provisioned (smsConfigured). Until then SMS is
  // "Coming soon" everywhere and the consent checkbox stays hidden. Shares
  // the reminder-settings cache key; a 403/offline failure = hidden.
  const { data: reminderSettings } = useQuery(
    ['reminder-settings'],
    () => reminderService.getSettings(),
    { retry: false, staleTime: 5 * 60 * 1000 },
  );
  const smsConsentUiLive = reminderSettings?.smsConfigured === true;

  const { data: clientsPage, isLoading } = useQuery(
    ['clients', { page: page + 1, limit: rowsPerPage, search: debouncedSearch, status: statusFilter }],
    withOfflineFallback(
      () =>
        clientApi.listPaginated({
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
        }),
      () =>
        readClientsPage({
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
        }) as any,
    ),
    {
      keepPreviousData: true,
      staleTime: 30_000,
    },
  );
  const clients: Client[] = clientsPage?.data ?? [];
  const totalClients = clientsPage?.total ?? 0;

  // Fetch horses only when the link dialog is open
  const { data: horses = [] } = useQuery<Horse[]>(
    'horses',
    async () => {
      const response = await api.get('/horses');
      return response.data;
    },
    { enabled: isLinkHorseDialogOpen, staleTime: 30_000 },
  );

  // Current owner links + percentages for the picked horse. HorseClient is
  // the authoritative split leg — the Horse.owners JSON can lack percentages
  // for legacy client-side links. Online-only, like the link itself.
  const {
    data: horseOwnerLinks = NO_OWNER_LINKS,
    isLoading: horseOwnersLoading,
    isError: horseOwnersError,
  } = useQuery<Array<{ clientId: string; name: string; ownershipPercentage: number | null }>>(
    ['horse-owners', linkHorseData.horseId],
    async () => (await api.get(`/horses/${linkHorseData.horseId}/owners`)).data,
    { enabled: isLinkHorseDialogOpen && !!linkHorseData.horseId },
  );

  // Equal split with the last owner taking the rounding remainder (mirrors
  // the patient-form distribution).
  const equalSplitFor = (ids: string[]): Record<string, string> => {
    const each = Math.floor(10000 / ids.length) / 100;
    const result: Record<string, string> = {};
    ids.forEach((id, i) => {
      const pct = i === ids.length - 1
        ? Math.round((100 - each * (ids.length - 1)) * 100) / 100
        : each;
      result[id] = String(pct);
    });
    return result;
  };

  // Prefill the allocation table whenever the picked horse (or its owner
  // list) changes: existing owners show their CURRENT stored percentages
  // (never silently replaced by an equal split), and the new client gets
  // whatever room is left (blank when the existing set already sums to 100,
  // forcing an explicit decision). Gated on the query settling — while
  // loading, `data` is undefined and the [] default is a fresh reference
  // every render, which would otherwise re-run this into a render storm.
  useEffect(() => {
    if (!isLinkHorseDialogOpen || !selectedClient || !linkHorseData.horseId) return;
    if (horseOwnersLoading || horseOwnersError) return;
    if (horseOwnerLinks.some((o) => o.clientId === selectedClient.id)) return; // already an owner — no allocation
    const prefill: Record<string, string> = {};
    let existingSum = 0;
    horseOwnerLinks.forEach((o) => {
      prefill[o.clientId] = o.ownershipPercentage != null ? String(o.ownershipPercentage) : '';
      existingSum += o.ownershipPercentage ?? 0;
    });
    // remainder 0 is a valid prefill: linking a new owner to a fully-allocated
    // horse defaults them to a 0% (non-billing) share.
    const remainder = Math.round((100 - existingSum) * 100) / 100;
    prefill[selectedClient.id] = remainder >= 0 && remainder < 100 ? String(remainder) : '';
    setLinkAllocations(prefill);
  }, [isLinkHorseDialogOpen, selectedClient?.id, linkHorseData.horseId, horseOwnerLinks, horseOwnersLoading, horseOwnersError]);

  const clientAlreadyOwnsLinkHorse = !!selectedClient &&
    horseOwnerLinks.some((o) => o.clientId === selectedClient.id);
  const linkAllocationTotal = Object.values(linkAllocations)
    .reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  // 0% is a legal share (owner of record, no billing responsibility), but a
  // BLANK field is not silently treated as 0 — every owner must be explicit.
  const linkAllocationsValid =
    horseOwnerLinks.length === 0 ||
    (Object.values(linkAllocations).every((v) => v.trim() !== '' && parseFloat(v) >= 0) &&
      Math.abs(linkAllocationTotal - 100) <= 0.01);

  // Fetch invoice stats for the selected client. Per-CLIENT stats are
  // deliberately NOT computed from the replica (HorseClient isn't replicated,
  // so an offline number would undercount) — skip the query offline and the
  // Financial Overview renders "—" with a caption instead of fake zeros.
  const { data: invoiceStats } = useQuery<InvoiceStats>(
    ['client-invoice-stats', selectedClient?.id],
    () => billingApi.getClientStats(selectedClient!.id),
    { enabled: !!selectedClient && skipWhenOffline() }
  );

  // BILL-CRED: available account credit (ledger-computed balance) — online-only,
  // same offline treatment as the invoice stats above.
  const { data: creditLedger, isError: creditLedgerError } = useQuery(
    ['client-credits', selectedClient?.id],
    () => creditsApi.ledger(selectedClient!.id),
    { enabled: !!selectedClient && skipWhenOffline() }
  );

  // Fetch Stripe Connect status — network-only probe; skipped offline (state
  // stays null/unknown) and re-run on reconnect.
  useEffect(() => {
    if (isOffline) return;
    const fetchStripeStatus = async () => {
      try {
        const response = await api.get('/stripe-connect/status');
        setStripeConnectEnabled(response.data.chargesEnabled === true);
      } catch (error) {
        setStripeConnectEnabled(false);
      }
    };
    fetchStripeStatus();
  }, [isOffline]);

  // Handle navigation with selectedClientId in state (e.g., coming back from
  // billing page). Now fetches the client directly since the full list may
  // not be loaded (server-side pagination).
  // A link opens its client ONCE per arrival: react-router keeps its own copy of location.state
  // (the raw replaceState never reaches it), so each list reload used to reopen the client.
  const openedLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const state = location.state as { selectedClientId?: string } | null;
    const arrival = location.key;
    if (!state?.selectedClientId || openedLinkRef.current === arrival) return;
    const open = (c: Client) => {
      if (openedLinkRef.current === arrival) return; // a late fetch after the list opened it
      openedLinkRef.current = arrival;
      setSelectedClient(c);
      window.history.replaceState({}, document.title);
    };
    const inPage = clients.find((c) => c.id === state.selectedClientId);
    if (inPage) {
      open(inPage);
    } else {
      clientApi
        .get(state.selectedClientId)
        .then(open)
        .catch(() => {
          /* client not found — ignore */
        });
    }
  }, [location.state, location.key, clients]);

  // Deep-link from the patient chart's owner control when a single Client id
  // couldn't be resolved (e.g. duplicate names or unsynced legacy owners):
  // open the list pre-filtered to the owner's name so the user can pick.
  useEffect(() => {
    const state = location.state as { selectedClientSearch?: string } | null;
    const term = state?.selectedClientSearch;
    if (!term) return;
    // Cancel any in-flight debounce so a previously-typed term can't fire
    // after us and clobber the deep-linked search.
    clearTimeout(debounceTimer.current);
    setSearchTerm(term);
    setDebouncedSearch(term);
    setPage(0);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  // Create client mutation
  const createClient = useMutation(
    // Consent fields never ride the create/update payloads (server strips
    // them anyway, ADR-0022) — handleSubmit posts them separately.
    async (data: Omit<typeof formData, 'smsConsent' | 'smsConsentSource'>) => {
      return executeWrite<Client>({
        entityType: 'client',
        table: 'clients',
        payload: data,
        networkFn: () => clientApi.create(data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients']);
        setIsDialogOpen(false);
        setSnackbar({ open: true, message: 'Client created successfully', severity: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          setSnackbar({ open: true, message: error.message, severity: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Failed to create client', severity: 'error' });
      },
    }
  );

  // Update client mutation
  const updateClient = useMutation(
    async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      return executeUpdate<Client>({
        entityType: 'client',
        table: 'clients',
        entityId: id,
        payload: data,
        networkFn: () => clientApi.update(id, data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients']);
        setIsDialogOpen(false);
        if (selectedClient) {
          queryClient.invalidateQueries(['client-detail', selectedClient.id]);
        }
        setSnackbar({ open: true, message: 'Client updated successfully', severity: 'success' });
      },
      onError: (error: any) => {
        if (error instanceof OfflineWriteBlockedError || error?.name === 'OfflineWriteBlockedError') {
          setSnackbar({ open: true, message: error.message, severity: 'error' });
          return;
        }
        setSnackbar({ open: true, message: 'Failed to update client', severity: 'error' });
      },
    }
  );

  // Delete client mutation
  const deleteClient = useMutation(
    async (id: string) => {
      return clientApi.delete(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients']);
        setSelectedClient(null);
        setSnackbar({ open: true, message: 'Client deleted successfully', severity: 'success' });
      },
      onError: (error: any) => {
        // A 409 HAS_HISTORY (history added since the dialog opened) carries the sentence to show.
        const message = error?.response?.data?.code === 'HAS_HISTORY'
          ? error.response.data.message
          : 'Failed to delete client';
        setSnackbar({ open: true, message, severity: 'error' });
      },
    }
  );

  // Link horse mutation
  const linkHorse = useMutation(
    async ({ clientId, horseId, role, allocations }: {
      clientId: string;
      horseId: string;
      role: HorseClientRole;
      allocations?: Array<{ clientId: string; ownershipPercentage: number }>;
    }) => {
      return clientApi.linkHorse(clientId, horseId, role, allocations);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients']);
        queryClient.invalidateQueries(['horse-owners']);
        if (selectedClient) {
          queryClient.invalidateQueries(['client-detail', selectedClient.id]);
        }
        closeLinkHorseDialog();
        setSnackbar({ open: true, message: 'Horse linked successfully', severity: 'success' });
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Failed to link horse';
        setSnackbar({ open: true, message: Array.isArray(message) ? message.join(', ') : message, severity: 'error' });
      },
    }
  );

  // Unlink horse mutation
  const unlinkHorse = useMutation(
    async ({ clientId, horseId, role }: { clientId: string; horseId: string; role: HorseClientRole }) => {
      return clientApi.unlinkHorse(clientId, horseId, role);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients']);
        if (selectedClient) {
          queryClient.invalidateQueries(['client-detail', selectedClient.id]);
        }
        setSnackbar({ open: true, message: 'Horse unlinked successfully', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Failed to unlink horse', severity: 'error' });
      },
    }
  );

  // Fetch full client details when a client is selected
  const { data: selectedClientDetails } = useQuery<Client>(
    ['client-detail', selectedClient?.id],
    withOfflineFallback(
      () => clientApi.get(selectedClient!.id),
      () => readClient(selectedClient!.id) as any,
    ),
    { enabled: !!selectedClient?.id, staleTime: 30_000 },
  );
  useEffect(() => {
    if (selectedClientDetails) {
      setSelectedClient(selectedClientDetails);
    }
  }, [selectedClientDetails]);

  const handleOpenDialog = async (client?: Client) => {
    try {
      if (client) {
        // Refetch the latest client data to ensure we have the current status.
        // Offline: hydrate the form from the replica so the (queued) update
        // path stays reachable; throw if the client isn't replicated so the
        // catch below still surfaces the failure.
        const latestClient = await withOfflineFallback(
          () => clientApi.get(client.id),
          () =>
            readClient(client.id).then((c) => {
              if (!c) throw new Error('client not in offline replica');
              return c as unknown as Client;
            }),
        )();
        setFormData({
          name: latestClient.name,
          email: latestClient.email || '',
          phone: latestClient.phone || '',
          billingAddress: latestClient.billingAddress || '',
          status: latestClient.status || ClientStatus.ACTIVE,
          smsEnabled: latestClient.smsEnabled ?? true,
          emailEnabled: latestClient.emailEnabled ?? true,
          contacts: (latestClient.contacts ?? []).map((c) => ({
            name: c.name ?? '',
            role: c.role ?? '',
            phone: c.phone ?? '',
            email: c.email ?? '',
          })),
          smsConsent: !!latestClient.smsConsentAt,
          smsConsentSource: latestClient.smsConsentSource ?? '',
        });
        setEditingClient(latestClient);
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          billingAddress: '',
          status: ClientStatus.ACTIVE,
          smsEnabled: true,
          emailEnabled: true,
          contacts: [],
          smsConsent: false,
          smsConsentSource: '',
        });
        setEditingClient(null);
      }
      setIsDialogOpen(true);
    } catch (error) {
      console.error('Error opening client dialog:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to open client form. Please try again.', 
        severity: 'error' 
      });
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingClient(null);
  };

  const handleSubmit = async () => {
    // Trim contact rows and drop half-filled ones (no name). Always send the
    // array — even empty — so removing every contact persists as a clear.
    const contacts = formData.contacts
      .map((c) => {
        const entry: ClientContact = { name: (c.name ?? '').trim() };
        const role = c.role?.trim();
        const phone = c.phone?.trim();
        const email = c.email?.trim();
        if (role) entry.role = role;
        if (phone) entry.phone = phone;
        if (email) entry.email = email;
        return entry;
      })
      .filter((c) => c.name.length > 0);
    // Consent never rides the PATCH (server strips it anyway, ADR-0022) — it
    // goes to the dedicated stamped endpoint, and ONLY when it changed.
    const { smsConsent, smsConsentSource, ...clientFields } = formData;
    const payload = { ...clientFields, contacts };
    if (editingClient) {
      // Only when the consent UI is live (platform-gated) — a hidden checkbox
      // must never drive the consent endpoint.
      const consentChanged =
        smsConsentUiLive && smsConsent !== !!editingClient.smsConsentAt;
      if (consentChanged) {
        if (!skipWhenOffline()) {
          setSnackbar({
            open: true,
            message: 'Recording text-message consent needs a connection — other changes were saved.',
            severity: 'error',
          });
        } else {
          try {
            await clientApi.recordSmsConsent(
              editingClient.id,
              smsConsent,
              smsConsentSource.trim() || undefined,
            );
          } catch {
            setSnackbar({
              open: true,
              message: 'Failed to record text-message consent — other changes were saved.',
              severity: 'error',
            });
          }
        }
      }
      updateClient.mutate({ id: editingClient.id, data: payload });
    } else {
      createClient.mutate(payload);
    }
  };

  const handleDelete = async (id: string) => {
    // Client delete is a HARD CASCADE (removes horses/records/invoices) — it can
    // NEVER be queued offline. Guard the handler so it can't run while offline.
    if (!skipWhenOffline()) {
      setSnackbar({
        open: true,
        message: 'Deleting a client is unavailable offline — reconnect to delete.',
        severity: 'error',
      });
      return;
    }
    try {
      // Fetch delete info to show proper warnings
      const info = await clientApi.getDeleteInfo(id);
      setDeleteInfo(info);
      setDeleteDialogOpen(true);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load delete information', severity: 'error' });
    }
  };

  const confirmDelete = () => {
    if (deleteInfo && selectedClient) {
      deleteClient.mutate(selectedClient.id);
      setDeleteDialogOpen(false);
      setDeleteInfo(null);
    }
  };

  const markInactiveInstead = () => {
    if (selectedClient) {
      updateClient.mutate({ id: selectedClient.id, data: { status: ClientStatus.INACTIVE } });
    }
    cancelDelete();
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteInfo(null);
  };

  const handleOpenLinkHorseDialog = () => {
    // Linking writes the non-replicated HorseClient relation — it can't be
    // queued offline, so block the dialog (same pattern as handleDelete)
    // instead of opening a dead-end picker.
    if (!skipWhenOffline()) {
      setSnackbar({
        open: true,
        message: 'Linking a horse is unavailable offline — reconnect to link.',
        severity: 'error',
      });
      return;
    }
    setIsLinkHorseDialogOpen(true);
  };

  const closeLinkHorseDialog = () => {
    setIsLinkHorseDialogOpen(false);
    setLinkHorseData({ horseId: '' });
    setLinkAllocations({});
  };

  const handleLinkHorse = () => {
    if (!selectedClient || !linkHorseData.horseId) return;
    // For a horse with existing owners, send the full allocation (existing +
    // new, validated to 100% server-side). A first owner needs none — the
    // backend assigns 100%.
    const allocations = horseOwnerLinks.length > 0
      ? [...horseOwnerLinks.map((o) => o.clientId), selectedClient.id].map((id) => ({
          clientId: id,
          ownershipPercentage: parseFloat(linkAllocations[id]) || 0,
        }))
      : undefined;
    linkHorse.mutate({
      clientId: selectedClient.id,
      horseId: linkHorseData.horseId,
      role: HorseClientRole.OWNER,
      allocations,
    });
  };

  const handleUnlinkHorse = (horseId: string, role: HorseClientRole) => {
    if (selectedClient && window.confirm('Are you sure you want to unlink this horse?')) {
      unlinkHorse.mutate({
        clientId: selectedClient.id,
        horseId,
        role,
      });
    }
  };

  const getStatusColor = (status: ClientStatus) => {
    switch (status) {
      case ClientStatus.ACTIVE:
        return 'success';
      case ClientStatus.INACTIVE:
        return 'default';
      case ClientStatus.ON_HOLD:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Sort operates on the current page only — backend orders by name asc by
  // default. Global sort would require a `sortBy` query param on /clients,
  // which we can add later if needed.
  const sortedClients = useMemo(() => {
    const sorted = [...clients].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortField === 'email') cmp = (a.email || '').localeCompare(b.email || '');
      else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [clients, sortField, sortDir]);

  // Server already paginates; the rows we render are just the sorted page.
  const paginatedClients = sortedClients;
  const totalPages = Math.max(1, Math.ceil(totalClients / rowsPerPage));

  const handleSort = (field: 'name' | 'email' | 'createdAt') => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const formatCurrency = formatDollars;

  // Empty state — no clients anywhere in the clinic (use server total).
  if (!isLoading && totalClients === 0 && !searchTerm && statusFilter === 'all') {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Avatar sx={{ 
            width: 120, 
            height: 120, 
            bgcolor: 'primary.light',
            mb: 3,
          }}>
            <PeopleIcon sx={{ fontSize: 80, color: 'primary.main' }} />
          </Avatar>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            No Clients Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
            Start building your client database by adding your first client. You can manage their contact information, billing, and associated horses all in one place.
          </Typography>
          {/* The empty state keeps its explanation and loses its call to action: the text above
              is still true for somebody who may only look, the button never was. */}
          {can.add && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              data-tour="client-add-button"
            >
              Add Your First Client
            </Button>
          )}
        </Box>

        {/* Add/Edit Client Dialog - Also needed in empty state */}
        <Dialog 
          open={isDialogOpen}
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <TextField
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
              <TextField
                label="Billing Address"
                multiline
                rows={3}
                value={formData.billingAddress}
                onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
              />
              <ClientContactsEditor
                contacts={formData.contacts}
                onChange={(contacts) => setFormData({ ...formData, contacts })}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ClientStatus })}
                >
                  <MenuItem value={ClientStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={ClientStatus.INACTIVE}>Inactive</MenuItem>
                  <MenuItem value={ClientStatus.ON_HOLD}>On Hold</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.smsEnabled}
                    onChange={(e) => setFormData({ ...formData, smsEnabled: e.target.checked })}
                  />
                }
                label="Enable SMS notifications"
              />
              {editingClient && smsConsentUiLive && (
                <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.smsConsent}
                        onChange={(e) =>
                          setFormData({ ...formData, smsConsent: e.target.checked })
                        }
                      />
                    }
                    label="Client agreed to receive text reminders"
                  />
                  {formData.smsConsent && !editingClient.smsConsentAt && (
                    <TextField
                      fullWidth
                      size="small"
                      label="How was consent obtained?"
                      placeholder="e.g. verbal at visit, intake form"
                      value={formData.smsConsentSource}
                      onChange={(e) =>
                        setFormData({ ...formData, smsConsentSource: e.target.value })
                      }
                    />
                  )}
                  {editingClient.smsConsentAt && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      data-testid="client-sms-consent-recorded"
                    >
                      Consent recorded{' '}
                      {/* Client.smsConsentAt is an INSTANT stamped server-side (clients.service.ts
                          recordSmsConsent → new Date()); DATE-KINDS §1.3. Compliance-adjacent, so the
                          consent timestamp must read in the CLINIC's zone. */}
                      {formatInstant(editingClient.smsConsentAt, INSTANT_FORMATS.dateNumeric)}
                      {editingClient.smsConsentSource
                        ? ` — ${editingClient.smsConsentSource}`
                        : ''}
                      . Texts stop automatically if the client replies STOP.
                    </Typography>
                  )}
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.emailEnabled}
                    onChange={(e) => setFormData({ ...formData, emailEnabled: e.target.checked })}
                  />
                }
                label="Enable email notifications"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={!formData.name}>
              {editingClient ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100vh',
      overflow: 'hidden',
      p: 3
    }}>
      <Box sx={{ mb: 3, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h4" fontWeight={700}>
              Clients list
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isMobile && can.add && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                data-tour="client-add-button"
                sx={{
                  bgcolor: '#3B2E1A',
                  '&:hover': { bgcolor: '#2A2012' },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Add
              </Button>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          View, add, edit and delete your client&apos;s details.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ flexGrow: 1, minHeight: 0, height: 0 }}>
        {/* Client Table. Below md an open client replaces it (Back to List restores it): stacked,
            the details would start below the page's clipped area and never be seen. */}
        <Grid
          item
          xs={12}
          md={selectedClient ? 5 : 12}
          sx={{ height: '100%', display: isMobile && selectedClient ? 'none' : 'flex' }}
        >
          <Paper sx={{ 
            width: '100%',
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
          }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, mobile number or client ID"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  },
                }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={statusFilter}
                    displayEmpty
                    onChange={(e) => { setStatusFilter(e.target.value as ClientStatus | 'all'); setPage(0); }}
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.8125rem',
                      bgcolor: 'background.paper',
                    }}
                    renderValue={(val) => {
                      if (val === 'all') return 'Filters';
                      return val;
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value={ClientStatus.ACTIVE}>Active</MenuItem>
                    <MenuItem value={ClientStatus.INACTIVE}>Inactive</MenuItem>
                    <MenuItem value={ClientStatus.ON_HOLD}>On Hold</MenuItem>
                  </Select>
                </FormControl>
                {/* Wraps under Filters in a narrow list, and shrinks when even that line is under
                    180 px (a client open on a 900 px screen) instead of spilling onto the details. */}
                <FormControl size="small" sx={{ minWidth: 'min(180px, 100%)' }}>
                  <Select
                    value={`${sortField}-${sortDir}`}
                    onChange={(e) => {
                      const [f, d] = e.target.value.split('-') as ['name' | 'email' | 'createdAt', 'asc' | 'desc'];
                      setSortField(f);
                      setSortDir(d);
                    }}
                    sx={{ 
                      borderRadius: 2,
                      fontSize: '0.8125rem',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <MenuItem value="createdAt-desc">Created at (newest first)</MenuItem>
                    <MenuItem value="createdAt-asc">Created at (oldest first)</MenuItem>
                    <MenuItem value="name-asc">Name (A–Z)</MenuItem>
                    <MenuItem value="name-desc">Name (Z–A)</MenuItem>
                    <MenuItem value="email-asc">Email (A–Z)</MenuItem>
                    <MenuItem value="email-desc">Email (Z–A)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <TableContainer data-tour="client-list" sx={{ flexGrow: 1, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}>
                      <TableSortLabel
                        active={sortField === 'name'}
                        direction={sortField === 'name' ? sortDir : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    {!selectedClient && (
                      <>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}>
                          <TableSortLabel
                            active={sortField === 'email'}
                            direction={sortField === 'email' ? sortDir : 'asc'}
                            onClick={() => handleSort('email')}
                          >
                            Email
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}>
                          Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}>
                          Horses
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', color: 'text.secondary', py: 1.5 }}>
                          Balance Due
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedClients.map((client) => {
                    const isSelected = selectedClient?.id === client.id;
                    return (
                      <TableRow
                        key={client.id}
                        hover
                        selected={isSelected}
                        onClick={() => setSelectedClient(client)}
                        sx={{
                          cursor: 'pointer',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(139,94,52,0.06)',
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: 'rgba(139,94,52,0.1)',
                          },
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 38,
                                height: 38,
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                bgcolor: isSelected ? theme.palette.primary.main : '#C9772B',
                                color: '#fff',
                              }}
                            >
                              {getInitials(client.name)}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ fontSize: '0.875rem' }}>
                              {client.name}
                            </Typography>
                            {client.clientNumber && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace', letterSpacing: 0.5, whiteSpace: 'nowrap' }}
                              >
                                {client.clientNumber}
                              </Typography>
                            )}
                            {client.status === 'ACTIVE' && (
                              <Chip size="small" label="Active" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {client.status === 'INACTIVE' && (
                              <Chip size="small" label="Inactive" color="default" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                            {client.status === 'ON_HOLD' && (
                              <Chip size="small" label="On Hold" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        {!selectedClient && (
                          <>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8125rem' }}>
                                {client.email || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8125rem' }}>
                                {client.phone || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8125rem' }}>
                                {client.horseCount || 0}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 2 }}>
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.8125rem', fontWeight: client.currentBalance > 0 ? 600 : 400 }}>
                                {formatCurrency(client.currentBalance)}
                              </Typography>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              py: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}>
              <Button
                size="small"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                sx={{ minWidth: 32, px: 1, fontSize: '0.75rem' }}
              >
                Prev
              </Button>
              <Typography variant="caption" color="text.secondary">
                {page + 1} of {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                sx={{ minWidth: 32, px: 1, fontSize: '0.75rem' }}
              >
                Next
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Client Profile */}
        {selectedClient && (
          <Grid item xs={12} md={7} sx={{ height: '100%', display: 'flex' }}>
            <Paper sx={{ 
              width: '100%',
              overflow: 'auto',
              p: 3,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}>
              {isMobile && (
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={() => setSelectedClient(null)} 
                  sx={{ mb: 2 }}
                  variant="outlined"
                >
                  Back to List
                </Button>
              )}

              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'start', 
                mb: 3,
                pb: 2,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h5" fontWeight={700}>
                      {selectedClient.name}
                    </Typography>
                    {selectedClient.clientNumber && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace', letterSpacing: 0.5, whiteSpace: 'nowrap' }}
                      >
                        {selectedClient.clientNumber}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={selectedClient.status === 'ACTIVE' ? 'Active Client' : selectedClient.status}
                    color={getStatusColor(selectedClient.status)}
                    size="small"
                    sx={{ fontWeight: 500, mt: 1 }}
                  />
                </Box>
                <Stack direction="row" spacing={1}>
                  {can.edit && (
                    <IconButton
                      onClick={() => handleOpenDialog(selectedClient)}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {can.remove && (
                    <IconButton
                      onClick={() => handleDelete(selectedClient.id)}
                      disabled={!skipWhenOffline()}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': { bgcolor: 'error.light', color: 'error.main' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>
              </Box>

              {/* Contact Info - compact */}
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {selectedClient.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {selectedClient.email}
                    </Typography>
                  </Box>
                )}
                {selectedClient.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {selectedClient.phone}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {/* Additional contacts — spouse, family, "call about the appt" person */}
              {(selectedClient.contacts?.length ?? 0) > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Additional Contacts
                  </Typography>
                  <Stack spacing={1.5}>
                    {selectedClient.contacts!.map((contact, index) => (
                      <Box key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {contact.name}
                          </Typography>
                          {contact.role && (
                            <Chip label={contact.role} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
                          )}
                        </Box>
                        {contact.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{contact.phone}</Typography>
                          </Box>
                        )}
                        {contact.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">{contact.email}</Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Notifications */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Notifications
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ 
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: selectedClient.smsEnabled ? 'success.main' : 'grey.400',
                    }} />
                    <Typography variant="body2" fontSize="0.8125rem">SMS Alerts</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ 
                      width: 8, height: 8, borderRadius: '50%',
                      bgcolor: selectedClient.emailEnabled ? 'success.main' : 'grey.400',
                    }} />
                    <Typography variant="body2" fontSize="0.8125rem">Email Updates</Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Financial Overview */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AccountBalanceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" fontWeight={600}>Financial Overview</Typography>
                </Box>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 2, 
                  border: `1px solid ${theme.palette.divider}`,
                }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                    Current Balance
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary' }}>
                      {formatCurrency(selectedClient.currentBalance)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* credits.controller.ts `create` → manage:discounts, NOT the client slug. */}
                      {can.issueCredit && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIssueCreditOpen(true)}
                        disabled={isOffline}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          borderRadius: 1.5,
                        }}
                      >
                        Issue Credit
                      </Button>
                      )}
                      {/* A deep link into /billing, which <ScreenRoute> refuses without view:billing —
                          so offering it to somebody who cannot read invoices is a link to a refusal. */}
                      {can.openBilling && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ReceiptIcon />}
                        onClick={() => navigate(`/billing?clientId=${selectedClient.id}`)}
                        disabled={!invoiceStats || invoiceStats.totalInvoiceCount === 0}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          borderRadius: 1.5,
                        }}
                      >
                        View Invoices
                      </Button>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 4 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">Open Invoices</Typography>
                      <Typography variant="body1" fontWeight={700}>
                        {isOffline ? '—' : invoiceStats?.openInvoiceCount || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">Last Invoice</Typography>
                      {/* lastInvoiceDate is Invoice.issuedAt (billing.service.ts getClientStats),
                          a mechanical INSTANT (`@default(now())`, DATE-KINDS §1.4) — the day it
                          shows is the clinic-zone day of that moment, not a calendar day. */}
                      <Typography variant="body1" fontWeight={700} data-testid="client-last-invoice">
                        {isOffline
                          ? '—'
                          : invoiceStats?.lastInvoiceDate
                          ? formatInstant(invoiceStats.lastInvoiceDate, 'MMM dd')
                          : 'None'}
                      </Typography>
                    </Box>
                    {/* BILL-CRED: ledger-computed available credit */}
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">Available Credit</Typography>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        color={(creditLedger?.balanceCents ?? 0) > 0 ? 'success.main' : 'text.primary'}
                      >
                        {/* '—' until the ledger actually loaded: a failed fetch must
                            not render as a confident $0.00 for a client who may
                            hold a real balance (fail-loud rule for money data). */}
                        {isOffline || creditLedgerError || !creditLedger
                          ? '—'
                          : formatCurrency(creditLedger.balance)}
                      </Typography>
                    </Box>
                  </Box>
                  {isOffline && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Invoice stats need a connection
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Associated Horses */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Associated Horses ({selectedClient.horses?.length || 0})
                  </Typography>
                  {/* clients.controller.ts `linkHorseWithRole` gates on manage:PATIENTS — linking
                      is an ownership edit, so somebody who may edit a client is not thereby
                      allowed to re-own a horse. */}
                  {can.linkHorse && (
                    <IconButton
                      size="small"
                      onClick={handleOpenLinkHorseDialog}
                      // Linking writes the non-replicated HorseClient relation —
                      // online-only (the handler guard is the backstop)
                      disabled={!skipWhenOffline()}
                      sx={{ border: `1px solid ${theme.palette.divider}` }}
                    >
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
                {selectedClient.horses && selectedClient.horses.length > 0 ? (
                  <Stack spacing={1}>
                    {selectedClient.horses.map((horse) => (
                      <ListItemButton
                        key={horse.id}
                        // Straight to the chart — '/patients' + selectedHorseId
                        // state was never consumed by PatientList, stranding the
                        // user on the list to re-search the same patient.
                        //
                        // …unless the chart is a screen this person cannot open, in which case the
                        // row keeps its information and stops being a link: <ScreenRoute> would
                        // answer "You don't have access to view patients" on the other side.
                        onClick={
                          can.openPatientChart ? () => navigate(`/patients/${horse.id}`) : undefined
                        }
                        disableRipple={!can.openPatientChart}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          py: 1.5,
                          px: 2,
                          ...(can.openPatientChart ? {} : { cursor: 'default' }),
                          '&:hover': can.openPatientChart ? { bgcolor: 'action.hover' } : {},
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.200', color: 'text.secondary' }}>
                            <HoofIcon sx={{ fontSize: 18 }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight={600}>
                              {horse.name}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {formatBreed(horse) || 'Unknown breed'} ·{' '}
                              {horse.role === 'OWNER'
                                ? `Owner${horse.ownershipPercentage != null ? ` · ${horse.ownershipPercentage}%` : ''}`
                                : 'Trainer'}
                            </Typography>
                          }
                        />
                        <IconButton edge="end" size="small" sx={{ color: 'text.secondary' }}>
                          <ArrowBackIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />
                        </IconButton>
                      </ListItemButton>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ 
                    textAlign: 'center', 
                    py: 3,
                    px: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}>
                    <HoofIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No horses linked yet
                    </Typography>
                    {can.linkHorse && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<LinkIcon />}
                        onClick={handleOpenLinkHorseDialog}
                        disabled={!skipWhenOffline()}
                        sx={{ mt: 1 }}
                      >
                        Link Your First Horse
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* Payment Methods — Stripe-backed, so the whole section needs a
                  connection. Offline we show a connectivity notice instead of
                  the misleading "Set up Stripe" message (status is unknown). */}
              <Divider sx={{ my: 2 }} />
              {isOffline ? (
                <Alert severity="info">Saved payment methods need an internet connection</Alert>
              ) : (
                <PaymentMethodsList
                  clientId={selectedClient.id}
                  onAddNew={() => setSaveCardDialogOpen(true)}
                  stripeEnabled={stripeConnectEnabled === true}
                />
              )}

              {/* Client Notes */}
              <Divider sx={{ my: 2 }} />
              <ClientNotesPanel clientId={selectedClient.id} />
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Client Dialog */}
      <Dialog 
        open={isDialogOpen}
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            />
            <TextField
              label="Billing Address"
              multiline
              rows={3}
              value={formData.billingAddress}
              onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
            />
            <ClientContactsEditor
              contacts={formData.contacts}
              onChange={(contacts) => setFormData({ ...formData, contacts })}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value as ClientStatus })}
              >
                <MenuItem value={ClientStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={ClientStatus.INACTIVE}>Inactive</MenuItem>
                <MenuItem value={ClientStatus.ON_HOLD}>On Hold</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.smsEnabled}
                  onChange={(e) => setFormData({ ...formData, smsEnabled: e.target.checked })}
                />
              }
              label="SMS Notifications"
            />
            {editingClient && smsConsentUiLive && (
              <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.smsConsent}
                      onChange={(e) =>
                        setFormData({ ...formData, smsConsent: e.target.checked })
                      }
                    />
                  }
                  label="Client agreed to receive text reminders"
                />
                {formData.smsConsent && !editingClient.smsConsentAt && (
                  <TextField
                    fullWidth
                    size="small"
                    label="How was consent obtained?"
                    placeholder="e.g. verbal at visit, intake form"
                    value={formData.smsConsentSource}
                    onChange={(e) =>
                      setFormData({ ...formData, smsConsentSource: e.target.value })
                    }
                  />
                )}
                {editingClient.smsConsentAt && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    data-testid="client-sms-consent-recorded"
                  >
                    Consent recorded{' '}
                    {/* Client.smsConsentAt is an INSTANT stamped server-side (clients.service.ts
                        recordSmsConsent → new Date()); DATE-KINDS §1.3. Compliance-adjacent, so the
                        consent timestamp must read in the CLINIC's zone. */}
                    {formatInstant(editingClient.smsConsentAt, INSTANT_FORMATS.dateNumeric)}
                    {editingClient.smsConsentSource
                      ? ` — ${editingClient.smsConsentSource}`
                      : ''}
                    . Texts stop automatically if the client replies STOP.
                  </Typography>
                )}
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailEnabled}
                  onChange={(e) => setFormData({ ...formData, emailEnabled: e.target.checked })}
                />
              }
              label="Email Notifications"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name}>
            {editingClient ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Horse Dialog */}
      <Dialog
        open={isLinkHorseDialogOpen}
        onClose={closeLinkHorseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Horse to Client</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={horses}
              getOptionLabel={patientLabel}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{patientLabel(option)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatBreed(option)}
                    </Typography>
                  </Box>
                </li>
              )}
              onChange={(_, value) =>
                setLinkHorseData({ horseId: value?.id || '' })
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Horse" placeholder="Search by name or patient ID" required />
              )}
            />

            {linkHorseData.horseId && horseOwnersError ? (
              // Fail loudly: falling through to the "sole owner" caption on a
              // failed fetch would silently equal re-split unseen co-owners.
              <Alert severity="error">
                Couldn't load this horse's current owners — re-select the horse to retry.
              </Alert>
            ) : !linkHorseData.horseId || (horseOwnerLinks.length === 0 && !horseOwnersLoading) ? (
              <Typography variant="caption" color="text.secondary">
                Client will be linked as the owner of this horse.
              </Typography>
            ) : clientAlreadyOwnsLinkHorse ? (
              <Alert severity="info">
                {selectedClient?.name} is already an owner of this horse.
              </Alert>
            ) : horseOwnersLoading ? (
              <Typography variant="caption" color="text.secondary">
                Loading current owners…
              </Typography>
            ) : (
              // Multi-owner allocation: percentages drive invoice splits, so
              // adding a co-owner must state where everyone's share lands.
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Ownership split
                  </Typography>
                  <Button
                    size="small"
                    onClick={() =>
                      selectedClient &&
                      setLinkAllocations(
                        equalSplitFor([...horseOwnerLinks.map((o) => o.clientId), selectedClient.id]),
                      )
                    }
                  >
                    Distribute equally
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  This horse already has {horseOwnerLinks.length}{' '}
                  {horseOwnerLinks.length === 1 ? 'owner' : 'owners'}. Set each owner's share —
                  multi-owner invoices are split by these percentages.
                </Typography>
                <Stack spacing={1}>
                  {[...horseOwnerLinks.map((o) => ({ id: o.clientId, label: o.name })),
                    ...(selectedClient ? [{ id: selectedClient.id, label: `${selectedClient.name} (new)` }] : [])
                  ].map((owner) => (
                    <Box key={owner.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {owner.label}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={linkAllocations[owner.id] ?? ''}
                        onChange={(e) =>
                          setLinkAllocations((prev) => ({ ...prev, [owner.id]: e.target.value }))
                        }
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        sx={{ width: 120 }}
                      />
                    </Box>
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  color={linkAllocationsValid ? 'success.main' : 'error.main'}
                  sx={{ display: 'block', mt: 1 }}
                >
                  Total: {Math.round(linkAllocationTotal * 100) / 100}% (must equal 100%
                  {Object.values(linkAllocations).some((v) => !(parseFloat(v) > 0))
                    ? ' — every owner needs a share'
                    : ''})
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  sx={{ mt: 0.5, px: 0 }}
                  onClick={() => {
                    const horseId = linkHorseData.horseId;
                    closeLinkHorseDialog();
                    // PatientList opens its Edit Patient dialog for this horse
                    // — that's where the owners + percentages form lives.
                    navigate('/patients', { state: { editHorseId: horseId } });
                  }}
                >
                  Edit owners on the patient file instead
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLinkHorseDialog}>Cancel</Button>
          <Button
            onClick={handleLinkHorse}
            variant="contained"
            disabled={
              !linkHorseData.horseId ||
              clientAlreadyOwnsLinkHorse ||
              horseOwnersLoading ||
              horseOwnersError ||
              !linkAllocationsValid ||
              linkHorse.isLoading
            }
          >
            Link Horse
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={cancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            <Typography variant="h6">{deleteInfo?.hasHistory ? 'Mark Client Inactive' : 'Delete Client'}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteInfo?.hasHistory && (
            <Typography variant="body1">
              <strong>{deleteInfo.clientName}</strong> has invoices, account credit or statements, so they can't be
              deleted. {selectedClient?.status === ClientStatus.INACTIVE
                ? 'They are already inactive.'
                : 'Mark the client inactive instead. Their history stays on file.'}
            </Typography>
          )}
          {deleteInfo && !deleteInfo.hasHistory && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete <strong>{deleteInfo.clientName}</strong>?
              </Typography>

              {deleteInfo.horseCount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This client is associated with <strong>{deleteInfo.horseCount}</strong> horse{deleteInfo.horseCount > 1 ? 's' : ''} and will be removed from {deleteInfo.horseCount > 1 ? 'their' : 'its'} profile.
                  </Alert>

                  {deleteInfo.horses.some(h => h.isPrimaryOwner) && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        ⚠️ Primary Owner Warning
                      </Typography>
                      <Typography variant="body2">
                        This client is the <strong>primary owner</strong> of the following horse{deleteInfo.horses.filter(h => h.isPrimaryOwner).length > 1 ? 's' : ''}:
                      </Typography>
                    </Alert>
                  )}

                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Affected horses:
                  </Typography>
                  <List dense>
                    {deleteInfo.horses.map((horse) => (
                      <ListItem key={horse.id} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">{horse.name}</Typography>
                              {horse.isPrimaryOwner && (
                                <Chip 
                                  label="Primary Owner" 
                                  size="small" 
                                  color="error"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This action cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          {deleteInfo?.hasHistory ? (
            selectedClient?.status !== ClientStatus.INACTIVE && (
              <Button onClick={markInactiveInstead} variant="contained">
                Mark Inactive
              </Button>
            )
          ) : (
            <Button onClick={confirmDelete} variant="contained" color="error">
              Delete Client
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* FAB for mobile */}
      {isMobile && can.add && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => handleOpenDialog()}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Save Payment Method Dialog */}
      {selectedClient && (
        <SavePaymentMethodDialog
          open={saveCardDialogOpen}
          onClose={() => setSaveCardDialogOpen(false)}
          onSuccess={() => {
            setSaveCardDialogOpen(false);
            queryClient.invalidateQueries(['paymentMethods', selectedClient.id]);
            setSnackbar({ open: true, message: 'Card saved successfully', severity: 'success' });
          }}
          client={selectedClient}
        />
      )}

      {/* BILL-CRED: issue an account credit to this client */}
      {selectedClient && (
        <IssueCreditDialog
          open={issueCreditOpen}
          onClose={() => setIssueCreditOpen(false)}
          onSuccess={() => queryClient.invalidateQueries(['client-credits', selectedClient.id])}
          clientId={selectedClient.id}
          clientName={selectedClient.name}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients;

